/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Notification Hooks - React Query for User Notifications
 * ============================================================================
 *
 * Custom hooks for fetching notifications using React Query.
 * Notifications are filtered by the user's role on the backend.
 *
 * ============================================================================
 */

import { useQuery } from '@tanstack/react-query';
import { notificationService } from '@/services/notification.service';
import { NotificationSummary, emptyNotificationSummary } from '@/types/notification.types';
import { ApiError } from '@/services/api/types';

// Query keys for cache management
export const notificationKeys = {
  all: ['notifications'] as const,
  summary: () => [...notificationKeys.all, 'summary'] as const,
  count: () => [...notificationKeys.all, 'count'] as const,
  providers: () => [...notificationKeys.all, 'providers'] as const,
};

// Mock data for development when API is unavailable
const mockNotificationSummary: NotificationSummary = {
  attentionItems: [
    {
      id: '1',
      providerId: 'dashboard',
      category: 'ATTENTION',
      type: 'PENDING_REGISTRATION',
      title: 'pendingRegistration',
      description: 'pendingRegistration',
      count: 5,
      urgency: 'MEDIUM',
      route: '/superadmin/registrations',
      icon: 'FileText',
      color: '#3B82F6',
      createdAt: new Date().toISOString(),
    },
    {
      id: '2',
      providerId: 'dashboard',
      category: 'ATTENTION',
      type: 'EXPIRING_TRIAL',
      title: 'expiringTrial',
      description: 'expiringTrial',
      count: 3,
      urgency: 'LOW',
      route: '/superadmin/organizations?status=TRIAL',
      icon: 'Clock',
      color: '#F59E0B',
      createdAt: new Date().toISOString(),
    },
  ],
  activityItems: [
    {
      id: '3',
      providerId: 'dashboard',
      category: 'ACTIVITY',
      type: 'ORG_CREATE',
      title: 'Organization Created',
      description: 'New organization created: Conakry Motors',
      entityId: 'org-1',
      entityName: 'Conakry Motors',
      route: '/superadmin/organizations',
      icon: 'Building2',
      color: '#10B981',
      createdAt: new Date(Date.now() - 2 * 60 * 1000).toISOString(),
    },
    {
      id: '4',
      providerId: 'dashboard',
      category: 'ACTIVITY',
      type: 'SUPERADMIN_BOOTSTRAP',
      title: 'System Bootstrap',
      description: 'SuperAdmin bootstrapped via UI',
      route: '/superadmin',
      icon: 'Shield',
      color: '#8B5CF6',
      createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    },
  ],
  totalCount: 8,
};

/**
 * Hook to fetch notification summary for the current user.
 * Notifications are filtered by role on the backend.
 *
 * @param useMockData - Set to true to use mock data during development
 */
export function useNotifications(useMockData: boolean = false) {
  return useQuery<NotificationSummary, ApiError>({
    queryKey: notificationKeys.summary(),
    queryFn: async () => {
      if (useMockData) {
        // Simulate network delay for mock data
        await new Promise(resolve => setTimeout(resolve, 300));
        return mockNotificationSummary;
      }
      return await notificationService.getNotifications();
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // Refetch every minute
    retry: 1,
    // Return empty summary on error to prevent UI breakage
    placeholderData: emptyNotificationSummary,
  });
}

/**
 * Hook to fetch notification count only.
 * Lightweight hook for badge display.
 */
export function useNotificationCount() {
  return useQuery<number, ApiError>({
    queryKey: notificationKeys.count(),
    queryFn: notificationService.getNotificationCount,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
    retry: 1,
    placeholderData: 0,
  });
}

/**
 * Hook to fetch registered notification providers.
 * Useful for debugging and admin views.
 */
export function useNotificationProviders() {
  return useQuery<string[], ApiError>({
    queryKey: notificationKeys.providers(),
    queryFn: notificationService.getProviders,
    staleTime: 5 * 60 * 1000, // 5 minutes - providers don't change often
  });
}
